import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/cn';
import { PORTAL_NAV_ITEMS } from '@/shared/layout/nav-items';

interface PortalSidebarProps {
  mobileOpen: boolean;
  onClose: () => void;
}

export function PortalSidebar({ mobileOpen, onClose }: PortalSidebarProps) {
  const { t } = useTranslation();

  return (
    <>
      {mobileOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px] lg:hidden"
          aria-label={t('nav.closeMenu')}
          onClick={onClose}
        />
      ) : null}

      <aside
        className={cn(
          'portal-sidebar fixed top-0 start-0 z-50 flex h-full w-[17rem] flex-col shadow-xl transition-transform duration-300 ease-out',
          'lg:static lg:z-auto lg:h-auto lg:w-64 lg:translate-x-0 lg:shadow-none',
          mobileOpen
            ? 'max-lg:translate-x-0'
            : 'max-lg:-translate-x-full max-lg:rtl:translate-x-full',
        )}
        aria-label={t('portal.nav.sidebar')}
      >
        <div className="portal-sidebar-brand flex h-16 items-center gap-3 px-4">
          <div
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl shadow-sm"
            style={{ background: 'var(--gradient-primary)' }}
          >
            <span className="text-sm font-bold text-white">CP</span>
          </div>
          <div className="min-w-0">
            <span className="block truncate font-semibold text-foreground">{t('portal.name')}</span>
            <span className="block truncate text-xs text-muted-foreground">
              {t('portal.tagline')}
            </span>
          </div>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {PORTAL_NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                {...(item.exact ? { activeOptions: { exact: true as const } } : {})}
                className="portal-nav-link"
                activeProps={{ className: cn('portal-nav-link', 'portal-nav-link-active') }}
                onClick={onClose}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span>{t(item.labelKey)}</span>
              </Link>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
